import { Loader2 } from "lucide-react";
import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  type AdminContactPermissions,
  apiErrorMessage,
  useCreateContact,
  useUpdateContact,
} from "@/lib/api";

export type ContactDialogMode = "create" | "edit";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  accountId: string;
  mode: ContactDialogMode;
  /** The contact being edited. Ignored in create mode. */
  contact?: AdminContactPermissions;
}

const COPY: Record<ContactDialogMode, { title: string; description: string; submit: string }> = {
  create: {
    title: "Add contact",
    description:
      "Add a person at this account. Give them an email so they can sign in with Microsoft over MCP.",
    submit: "Add contact",
  },
  edit: {
    title: "Edit contact",
    description: "Update this person's details. Clearing email or role removes it.",
    submit: "Save",
  },
};

export function ContactDialog({ open, onOpenChange, accountId, mode, contact }: Props) {
  const createContact = useCreateContact(accountId);
  const updateContact = useUpdateContact(accountId);

  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("");
  const [error, setError] = useState("");

  const isPending = createContact.isPending || updateContact.isPending;

  // Seed the form from the contact each time the dialog opens.
  useEffect(() => {
    if (open) {
      setFullName(mode === "create" ? "" : (contact?.full_name ?? ""));
      setEmail(mode === "create" ? "" : (contact?.email ?? ""));
      setRole(mode === "create" ? "" : (contact?.role ?? ""));
      setError("");
    }
  }, [open, mode, contact]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    try {
      if (mode === "create") {
        await createContact.mutateAsync({
          fullName: fullName.trim(),
          email: email.trim() || null,
          role: role.trim() || null,
        });
      } else if (mode === "edit" && contact) {
        await updateContact.mutateAsync({
          contactId: contact.contact_id,
          fullName: fullName.trim(),
          // Explicit null clears the stored value when the field is emptied.
          email: email.trim() || null,
          role: role.trim() || null,
        });
      }
      onOpenChange(false);
    } catch (err) {
      setError(apiErrorMessage(err, "Something went wrong. Please try again."));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{COPY[mode].title}</DialogTitle>
          <DialogDescription>{COPY[mode].description}</DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="contactFullName" className="text-foreground text-sm font-medium">
              Full name
            </label>
            <Input
              id="contactFullName"
              placeholder="Dr. Jane Doe"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="contactEmail" className="text-foreground text-sm font-medium">
              Email <span className="text-muted font-normal">(their MCP sign-in)</span>
            </label>
            <Input
              id="contactEmail"
              type="email"
              placeholder="jane.doe@customer.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="contactRole" className="text-foreground text-sm font-medium">
              Role <span className="text-muted font-normal">(optional)</span>
            </label>
            <Input
              id="contactRole"
              placeholder="Procurement lead"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            />
          </div>

          {error && <p className="text-destructive text-sm">{error}</p>}
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending || !fullName.trim()}>
              {isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : COPY[mode].submit}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
